import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LightGestion from 'components/headers/lightGestion';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Button, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const GestionCredit = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'debtors' (debtors means clients that have credit !== 0)
  const [showAdjust, setShowAdjust] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [adjust, setAdjust] = useState({ type: 'add', clientId: null, amount: '', note: '' });
  const [payment, setPayment] = useState({ clientId: null, amount: '', note: '' });

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/clientgestions', { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || res.data.clients || []);
      const data = (raw || []).map(r => ({
        id: r.id || r.client_id || r._id || r.ID,
        name: r.name || r.Nom || r.nom || '',
        phone: r.phone || r.Telephone || r.telephone || r.Phone || '',
        address: r.address || r.Address || r.adresse || '',
        credit: (r.credit !== undefined && r.credit !== null) ? Number(r.credit) : (r.Credit !== undefined ? Number(r.Credit) : 0),
        _raw: r
      }));
      setClients(data);
    } catch (e) {
      console.error(e);
      Swal.fire(t('credit.alerts.error', 'Erreur'), t('credit.alerts.fetchFailed', 'Impossible de récupérer les clients'));
    }
  }, [t]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const totals = {
    total: clients.length,
    debtors: clients.filter(c => Number(c.credit || 0) !== 0).length,
    totalCredit: clients.reduce((s,c) => s + (Number(c.credit || 0)), 0),
  };

  const visible = clients.filter(c => {
    const q = search.trim().toLowerCase();
    // apply text search
    const matchesSearch = !q || (c.name || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    // apply filter button: debtors = clients that have any credit (non-zero)
    if (filter === 'debtors') {
      return Number(c.credit || 0) !== 0;
    }
    return true;
  });

  const openAdjustFor = (client) => {
    setSelectedClient(client || null);
    setAdjust({ ...adjust, clientId: client?.id || null, amount: '', type: 'add', note: '' });
    setShowAdjust(true);
  };

  const openPaymentFor = (client) => {
    setSelectedClient(client || null);
    setPayment({ ...payment, clientId: client?.id || null, amount: '', note: '' });
    setShowPayment(true);
  };

  const handleAdjustSubmit = async () => {
    const clientIdToUse = adjust.clientId || selectedClient?.id;
    if (!clientIdToUse) return Swal.fire(t('credit.alerts.error', 'Erreur'), t('credit.alerts.selectClient', 'Sélectionnez un client'));
    try {
      const token = localStorage.getItem('authToken');
      const payload = { amount: Number(adjust.amount || 0), type: adjust.type, note: adjust.note };
      // Try endpoint per-client first
      try {
        await axios.post(`https://plateformewmg.cloud/api/Gestion/clientgestions/${clientIdToUse}/adjust-credit`, payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      } catch (e) {
        // fallback to generic endpoint
        await axios.post(`https://plateformewmg.cloud/api/Gestion/clientgestions/adjust-credit`, { client_id: clientIdToUse, ...payload }, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      }
      Swal.fire(t('credit.alerts.success', 'Succès'), t('credit.alerts.adjustSaved', 'Ajustement enregistré'), 'success');
      setShowAdjust(false);
      fetchClients();
    } catch (e) {
      console.error(e);
      Swal.fire(t('credit.alerts.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  const handlePaymentSubmit = async () => {
    const clientIdToUse = payment.clientId || selectedClient?.id;
    if (!clientIdToUse) return Swal.fire(t('credit.alerts.error', 'Erreur'), t('credit.alerts.selectClient', 'Sélectionnez un client'));
    try {
      const token = localStorage.getItem('authToken');
      const payload = { amount: Number(payment.amount || 0), note: payment.note };
      try {
        await axios.post(`https://plateformewmg.cloud/api/Gestion/clientgestions/${clientIdToUse}/payments`, payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      } catch (e) {
        await axios.post(`https://plateformewmg.cloud/api/Gestion/clientgestions/payment`, { client_id: clientIdToUse, ...payload }, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      }
      Swal.fire(t('credit.alerts.success', 'Succès'), t('credit.alerts.paymentSaved', 'Paiement enregistré'), 'success');
      setShowPayment(false);
      fetchClients();
    } catch (e) {
      console.error(e);
      Swal.fire(t('credit.alerts.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  return (
    <AnimationRevealPage>
      <LightGestion />
      <div className="container  app-content" style={{ maxWidth: 1100 ,marginTop:"-60px",marginLeft:"-20px"}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontWeight: 900, fontSize: '30px', marginBottom: 6 }}>{t('credit.title', 'Clients Débiteurs')}</h2>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button variant="warning" onClick={() => setShowAdjust(true)}>{t('credit.buttons.adjust', 'Ajustement de Crédit')}</Button>
            <Button variant="success" onClick={() => setShowPayment(true)}>{t('credit.buttons.collect', 'Encaisser')}</Button>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('credit.searchPlaceholder', 'Rechercher un client...')} style={{ flex: 1, padding: 16, borderRadius: 8, border: '1px solid #e6e6e9' }} />
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant={filter === 'all' ? 'primary' : 'outline-secondary'} onClick={() => setFilter('all')}>{t('credit.filters.all', 'Tous')}</Button>
              <Button variant={filter === 'debtors' ? 'warning' : 'outline-warning'} onClick={() => setFilter('debtors')}>{t('credit.filters.debtors', 'Débiteurs')}</Button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 20 }}>
            <div style={{ background: '#fff', padding: 18, borderRadius: 8 }}>
              <div style={{ color: '#6b7280' }}>{t('credit.stats.totalClients', 'Total clients')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.total}</div>
            </div>
            <div style={{ background: '#fff', padding: 18, borderRadius: 8 }}>
              <div style={{ color: '#6b7280' }}>{t('credit.stats.debtors', 'Débiteurs')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.debtors}</div>
            </div>
            <div style={{ background: '#fff', padding: 18, borderRadius: 8 }}>
              <div style={{ color: '#6b7280' }}>{t('credit.stats.totalCredit', 'Crédit total')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.totalCredit.toFixed(2)} {t('stock.currency', 'MAD')}</div>
            </div>
          </div>

          <div style={{ marginTop: 20, background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px 160px', gap: 12, padding: 18, background: '#fafafa', color: '#6b7280', fontWeight: 700 }}>
              <div>{t('credit.headers.name', 'NOM DU CLIENT')}</div>
              <div>{t('credit.headers.phone', 'TÉLÉPHONE')}</div>
              <div>{t('credit.headers.dueAmount', 'MONTANT DÛ')}</div>
              <div>{t('credit.headers.actions', 'ACTIONS')}</div>
            </div>

            <div>
              {visible.map((c, idx) => (
                <div key={c.id || idx} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px 160px', gap: 12, padding: 18, alignItems: 'center', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 22, background: '#6b8cff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(c.name || ' ')[0]?.toUpperCase() || 'U'}</div>
                    <div>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div style={{ color: '#9ca3af' }}>{c.address || t('credit.fallbacks.addressMissing', 'Adresse non renseignée')}</div>
                    </div>
                  </div>
                  <div style={{ color: '#2563eb' }}>{c.phone || t('credit.fallbacks.phoneNA', 'N/A')}</div>
                  <div style={{ textAlign: 'center', fontWeight: 700, color: Number(c.credit || 0) >= 0 ? '#10b981' : '#ef4444' }}>{Number(c.credit || 0).toFixed(2)} {t('stock.currency', 'MAD')}</div>
                  <div style={{ textAlign: 'center' }}>
                    <Button variant="outline-success" size="sm" onClick={() => openPaymentFor(c)}>{t('credit.row.collect', 'Encaisser')}</Button>{' '}
                    <Button variant="outline-primary" size="sm" onClick={() => openAdjustFor(c)}>{t('credit.row.adjust', 'Crédit')}</Button>{' '}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>


      {/* Ajustement modal */}
      <Modal show={showAdjust} onHide={() => setShowAdjust(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('credit.modals.adjust.title', 'Ajustement de Crédit Manuel')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>{t('credit.modals.adjust.searchLabel', 'Rechercher un Client')}</Form.Label>
              <Form.Control list="client-list" placeholder={t('credit.modals.adjust.searchPlaceholder', 'Tapez le nom du client...')} value={selectedClient?.name || ''} onChange={(e) => {
                const name = e.target.value;
                const found = clients.find(x => (x.name || '').toLowerCase() === name.toLowerCase());
                if (found) {
                  setSelectedClient(found);
                  setAdjust({ ...adjust, clientId: found.id });
                } else {
                  setSelectedClient({ name });
                  setAdjust({ ...adjust, clientId: null });
                }
              }} />
              <datalist id="client-list">
                {clients.map(c => (<option key={c.id || c._raw?.id || c.name} value={c.name} />))}
              </datalist>
            </Form.Group>
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <Form.Check type="radio" label={t('credit.modals.adjust.addRadio', 'Ajouter crédit')} name="adjtype" checked={adjust.type === 'add'} onChange={() => setAdjust({...adjust, type: 'add'})} />
              <Form.Check type="radio" label={t('credit.modals.adjust.removeRadio', 'Retirer crédit')} name="adjtype" checked={adjust.type === 'remove'} onChange={() => setAdjust({...adjust, type: 'remove'})} />
            </div>
            <Form.Group className="mt-3">
              <Form.Label>{t('credit.modals.adjust.amountLabel', 'Montant à ajouter / retirer')}</Form.Label>
              <Form.Control type="number" value={adjust.amount} onChange={(e) => setAdjust({...adjust, amount: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('credit.modals.adjust.noteLabel', 'Motif (ex: Report ancien solde)')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={adjust.note} onChange={(e) => setAdjust({...adjust, note: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdjust(false)}>{t('credit.modals.common.cancel', 'Annuler')}</Button>
          <Button variant="success" onClick={handleAdjustSubmit}>{t('credit.modals.common.save', 'Enregistrer')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Payment modal */}
      <Modal show={showPayment} onHide={() => setShowPayment(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('credit.modals.payment.title', 'Enregistrer un Paiement')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
              <Form.Group>
                <Form.Label>{t('credit.modals.payment.clientLabel', 'Client')}</Form.Label>
                <Form.Control list="client-list-payment" placeholder={t('credit.modals.payment.clientPlaceholder', 'Sélectionner un client...')} value={selectedClient?.name || ''} onChange={(e) => {
                  const name = e.target.value;
                  const found = clients.find(x => (x.name || '').toLowerCase() === name.toLowerCase());
                  if (found) {
                    setSelectedClient(found);
                    setPayment({ ...payment, clientId: found.id });
                  } else {
                    setSelectedClient({ name });
                    setPayment({ ...payment, clientId: null });
                  }
                }} />
                <datalist id="client-list-payment">
                  {clients.map(c => (<option key={(c.id || c._raw?.id || c.name) + '-pay'} value={c.name} />))}
                </datalist>
              </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('credit.modals.payment.amountLabel', 'Montant du paiement')}</Form.Label>
              <Form.Control type="number" value={payment.amount} onChange={(e) => setPayment({...payment, amount: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('credit.modals.payment.noteLabel', 'Note (optionnel)')}</Form.Label>
              <Form.Control as="textarea" rows={2} value={payment.note} onChange={(e) => setPayment({...payment, note: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayment(false)}>{t('credit.modals.common.cancel', 'Annuler')}</Button>
          <Button variant="success" onClick={handlePaymentSubmit}>{t('credit.modals.payment.validate', 'Valider le Paiement')}</Button>
        </Modal.Footer>
      </Modal>
    </AnimationRevealPage>
  );
};

export default GestionCredit;
